import { useEffect } from 'react';
import { ButtonHeadless, Modal, Spin } from '@wajeez-web/components/ui';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  FormTextInput,
  FormSelectInput,
  FormAreaTextInput,
  FormDateInput,
} from '@wajeez-web/components/form-builder';
import { Server } from '@wajeez/wajeez.microservices';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';
import { BiLoaderAlt } from 'react-icons/bi';
import {
  PromoCodeAliasesActions,
  PromoCodesActions,
  useProductDispatch,
  useProductSelector,
} from '@wajeez-web/stores/product';

type Input = Server.CreatePromoCodeAliasInput & {
  id?: number;
};

interface PromoCodeAliasModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: () => void | Promise<void>;
  id?: number;
}

export const PromoCodeAliasModal = ({
  isOpen,
  onClose,
  onSubmit,
  id,
}: PromoCodeAliasModalProps) => {
  const productDispatch = useProductDispatch();

  const createPromoCodeAliasStatus = useProductSelector(
    ({ promoCodeAliases }) => promoCodeAliases.createPromoCodeAlias.status
  );
  const editPromoCodeAliasStatus = useProductSelector(
    ({ promoCodeAliases }) => promoCodeAliases.editPromoCodeAlias.status
  );
  const promoCodeAliasById = useProductSelector(
    ({ promoCodeAliases }) => promoCodeAliases.promoCodeAliasById
  );

  const promoCodesLookups = useProductSelector(
    ({ promoCodes }) => promoCodes.lookups
  );

  const { handleSubmit, control, reset } = useForm<Input>({
    defaultValues: {},
  });

  const onFormSubmit: SubmitHandler<Input> = async (data) => {
    let res: any;

    if (!id) {
      res = await productDispatch(
        PromoCodeAliasesActions.createPromoCodeAliasAsync(data)
      );
    } else {
      res = await productDispatch(
        PromoCodeAliasesActions.editPromoCodeAliasAsync({
          promoCodeAliasId: id,
          promoCodeId: data.promoCodeId,
          expiresOn: data.expiresOn,
          notes: data.notes,
        })
      );
    }

    if (res.meta.requestStatus === 'fulfilled') {
      onSubmit && (await onSubmit());
    }
  };

  useEffect(() => {
    if (promoCodeAliasById.data) {
      reset(promoCodeAliasById.data);
    }
  }, [promoCodeAliasById.data]);

  useEffect(() => {
    productDispatch(PromoCodesActions.getPromoCodesLookupsAsync());
  }, [productDispatch]);

  useEffect(() => {
    if (id) {
      productDispatch(PromoCodeAliasesActions.getPromoCodeAliasByIdAsync(id));
    }

    return () => {
      productDispatch(PromoCodeAliasesActions.clearPromoCodeAliasById());
    };
  }, [id, productDispatch]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title="Promo Code Alias"
      alertTarget="modal"
    >
      <div className="w-full">
        {promoCodesLookups.status === 'loading' ||
        promoCodeAliasById.status === 'loading' ? (
          <div className="w-full min-h-[200px] flex justify-center items-center">
            <Spin />
          </div>
        ) : (
          <form
            onSubmit={handleSubmit(onFormSubmit)}
            noValidate
            className="w-full flex flex-col justify-start items-start"
          >
            <div className="w-[300px] flex flex-col justify-start items-start space-y-2">
              <FormSelectInput
                name="promoCodeId"
                control={control}
                fieldType={{
                  fieldType: Server.FieldType.Select,
                  fieldOptions: {
                    selectOptions: promoCodesLookups.data?.map((p) => {
                      return {
                        value: p.id || '',
                        label: p.name || '',
                      };
                    }),
                  },
                  required: true,
                  label: 'Promo Code',
                }}
                errorMessages={{ required: 'This field is required' }}
                widthClassName="w-full"
                showErrors
              />
              {!id && (
                <FormTextInput
                  name="code"
                  control={control}
                  fieldType={{
                    fieldType: Server.FieldType.Text,
                    fieldOptions: {},
                    required: true,
                    label: 'Code',
                  }}
                  errorMessages={{ required: 'This field is required' }}
                  heightClassName="h-[34px]"
                  widthClassName="w-full"
                  showErrors
                />
              )}
              <div className="w-full">
                <FormAreaTextInput
                  name="notes"
                  control={control}
                  fieldType={{
                    fieldType: Server.FieldType.AreaText,
                    fieldOptions: {},
                    label: 'Notes',
                  }}
                  errorMessages={{ required: 'This field is required' }}
                  showErrors
                />
              </div>
              <div className="w-full">
                <FormDateInput
                  name="expiresOn"
                  control={control}
                  fieldType={{
                    fieldType: Server.FieldType.Date,
                    fieldOptions: {},
                    label: 'Expires On',
                  }}
                  errorMessages={{ required: 'This field is required' }}
                  showErrors
                />
              </div>
            </div>
            <div className="mt-6 w-full flex justify-end items-center space-s-2">
              <div className="w-[70px]">
                <ButtonHeadless
                  bgStyle="bg-warmGray-300"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                  action={onClose}
                >
                  <div className="text-warmGray-600 text-[16px] font-medium">
                    <ReturnTranslatedText
                      text="buttons.Cancel"
                      origintext="Cancel"
                    />
                  </div>
                </ButtonHeadless>
              </div>
              <div className="w-[70px]">
                <ButtonHeadless
                  type="submit"
                  borderStyle="border-none"
                  paddingYStyle="py-1"
                >
                  <div className="text-white text-[16px] font-medium flex justify-center items-center space-s-2">
                    <ReturnTranslatedText
                      text="buttons.Save"
                      origintext="Save"
                    />
                    {(createPromoCodeAliasStatus === 'loading' ||
                      editPromoCodeAliasStatus === 'loading') && (
                      <BiLoaderAlt className="animate-spin w-5" />
                    )}
                  </div>
                </ButtonHeadless>
              </div>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};
